import { connect } from 'formik';
import React, { Component, PropsWithChildren } from 'react';
import { Multiselect as RWMultiselect } from 'react-widgets';
import { RegisterElement } from '..';
import { getDescribedBy } from '../Input/inputUtils';
import { FieldHelpersWrapper, GlyphiconWrapper, wrapperClasses } from '../utils/formHelpers';
import { resolveProps } from '../utils/formikUtils';
import { createId } from '../utils/IdGenerator';
import { sizeStyles } from '../utils/sizes';
import './Multiselect.scss';
import { IMultiselectOnChange, IMultiselectProps, InnerMultiselectProps } from './types';

interface IMultiselectState {
  isOpen: boolean;
}

/**
 * Beta
 *
 * this is a wrapper for http://jquense.github.io/react-widgets/api/Multiselect/
 */
export class Multiselect extends Component<PropsWithChildren<InnerMultiselectProps>, IMultiselectState> {
  public static defaultProps = {
    data: [],
  };

  public state = {
    isOpen: false,
  };

  public id: string = this.props.id || createId('multiselect');

  public componentDidMount() {
    this.props.formik.registerField && this.props.formik.registerField(this.props.name, this);
    window.document.addEventListener('keydown', this.onEscape, true);
  }

  public componentWillUnmount() {
    this.props.formik.unregisterField && this.props.formik.unregisterField(this.props.name);
    window.document.removeEventListener('keydown', this.onEscape, true);
  }

  public handleChange = (dataItems: any[], metadata: any) => {
    const { name, onChange, formik } = this.props;
    const data: IMultiselectOnChange<any> = { dataItems, metadata };
    if (onChange) {
      onChange(name, data);
    } else if (Object.keys(formik).length && name && formik.values[name] !== undefined) {
      formik.setFieldValue(name, dataItems);
    }
  };

  public render() {
    const { id } = this;
    const {
      name,
      label,
      helpText,
      'qa-id': qa,
      data,
      displaySuccess = false,
      onCreate,
      textField,
      valueField,
      options,
      messages,
      onChange,
      sizePreset,
      ...props
    } = this.props;
    const {
      valid = true,
      touched = false,
      value = [],
      onBlur: formikOnBlur,
    } = resolveProps(this.props, this.props.formik);

    const showError = touched && !valid;
    const showSuccess = displaySuccess && touched && valid;
    const describedBy = getDescribedBy({
      hasErrors: showError,
      hasHelpText: !!helpText,
      id: this.id,
    });

    let dataProp: any[] | undefined;
    let textFieldProp: string | undefined | ((dataItem: any) => string);
    let valueFieldProp: string | undefined;
    let itemComponent: React.ComponentType;

    if (options) {
      dataProp = options;
      textFieldProp = 'display';
      valueFieldProp = 'value';
      itemComponent = ({
        item,
      }: {
        item: {
          value: string | number;
          display?: string;
          subtext?: string;
        };
      }) => {
        return (
          <React.Fragment>
            {item.display || item.value} {item.subtext ? <span className="subtext">{item.subtext}</span> : null}
          </React.Fragment>
        );
      };
    } else {
      dataProp = data;
      textFieldProp = textField;
      valueFieldProp = valueField;
    }

    const classes = wrapperClasses({
      className: 'hlc-multiselect',
      showError,
      showSuccess,
    });

    return (
      <RegisterElement name={`register-${id}`}>
        {(ref: any) => {
          const myRef = showError ? ref : undefined;
          return (
            <div ref={myRef} id={`${id}-wrapper`} className={classes} qa-id={qa} style={sizeStyles(sizePreset)}>
              <FieldHelpersWrapper id={id} props={this.props}>
                <div aria-describedby={describedBy}>
                  <GlyphiconWrapper {...{ showError, showSuccess, valid }}>
                    <RWMultiselect
                      busy={this.props.busy}
                      data={dataProp}
                      messages={messages}
                      onChange={this.handleChange}
                      onCreate={onCreate}
                      onToggle={this.onToggle}
                      open={this.state.isOpen}
                      value={value}
                      itemComponent={itemComponent}
                      textField={textFieldProp}
                      valueField={valueFieldProp}
                      inputProps={{
                        'data-testid': `hlc-multiselect-input-${id}`,
                        id,
                        name,
                        onBlur: (e: any) => {
                          if (formikOnBlur) formikOnBlur(e);
                        },
                      }}
                      {...props}
                    />
                  </GlyphiconWrapper>
                </div>
              </FieldHelpersWrapper>
            </div>
          );
        }}
      </RegisterElement>
    );
  }
  private onEscape = (e: KeyboardEvent) => {
    if (this.state.isOpen && e.keyCode === 27) {
      this.setState({ isOpen: false });
      e.stopPropagation();
    }
  };
  private onToggle = (data: boolean) => {
    this.setState({ isOpen: data });
  };
}

export default connect<IMultiselectProps>(Multiselect);
